import OSS from "ali-oss";
import agent from "../agent";

class UploadOSS {
  href = "";
  initReady = false;
  client;
  file;
  objectName;
  // 文件大小
  fileSize = 0;
  // 上传进度观察者
  progressObserverList = [];
  // 上传异常观察者
  errorObserverList = [];
  // 实时上传速度观察者
  netSpeedObserverList = [];
  // 是否上传成功
  isUploadReady = false;
  // 初始化完成，initClient完成
  created;
  errorMapping = {
    netError: { list: ["RequestError", "XHR error"], msg: "网络异常" },
  };

  constructor({
    file,
    progressListener,
    netSpeedListener,
    errorListener,
    didUpload,
    created,
    startUpload,
  }) {
    if (!file) {
      this.emitErrorListener(`实例化UploadOSS失败，缺少参数！`);
      return;
    }
    this.file = file;
    this.fileSize = file.size;
    typeof created === "function" && (this.created = created);
    typeof didUpload === "function" && (this.didUpload = didUpload);
    typeof startUpload === "function" && (this.startUpload = startUpload);
    typeof progressListener === "function" &&
      this.progressObserverList.push(progressListener);
    typeof netSpeedListener === "function" &&
      this.netSpeedObserverList.push(netSpeedListener);
    typeof errorListener === "function" &&
      this.errorObserverList.push(errorListener);
    this.fileTag = this.getFileTag(file);
    this.initClient();
  }

  /**
   * 通过fileTag获取保存本地的历史上传进度。
   * @returns 历史上传进度
   */
  getLocalStorageByTag = () => {
    if (!this.fileTag) return;
    const beforeAbortPoint = localStorage.getItem(this.fileTag);
    return JSON.parse(beforeAbortPoint || null);
  };

  /**
   * 获取文件唯一标识
   * @param {*} file
   */
  getFileTag = (file) => {
    if (!file) return "";
    const { lastModified, size, name } = file;
    return `${new Date(lastModified).getTime()}-${size}-${name}`;
  };

  /**
   * 初始化OSS实例
   */
  initClient = async () => {
    const res = await agent.SassPlatform.getSTS();
    if (res && res.success && res.code === "S0000") {
      const {
        data: {
          accessKeyId: ak,
          accessKeySecret: sk,
          securityToken: sessionToken,
          region,
          cloudBucketName,
          objectKey,
          endpointUrl: endPoint,
        },
      } = res;
      const abortCheckpoint = this.getLocalStorageByTag();
      this.client = new OSS({
        region,
        // 从STS服务获取的临时访问密钥（AccessKey ID和AccessKey Secret）。
        accessKeyId: ak,
        accessKeySecret: sk,
        // 从STS服务获取的安全令牌（SecurityToken）。
        stsToken: sessionToken,
        // 刷新临时访问凭证的时间间隔，单位为毫秒。
        refreshSTSTokenInterval: 20 * 60 * 1000,
        // 填写Bucket名称。
        bucket: cloudBucketName,
        refreshSTSToken: async () => {
          const res = await agent.SassPlatform.getSTS();
          if (res && res.success && res.code === "S0000") {
            const {
              data: {
                accessKeyId: ak,
                accessKeySecret: sk,
                securityToken: sessionToken,
              },
            } = res;
            return {
              accessKeyId: ak,
              accessKeySecret: sk,
              stsToken: sessionToken,
            };
          }
          this.emitErrorListener(`更新STS失败，请刷新页面重新上传。`);
        },
      });
      if (abortCheckpoint) {
        const { name } = abortCheckpoint;
        this.objectName = name;
      } else {
        this.objectName = objectKey;
      }
      this.initReady = true;
      this.href = `${window.location.protocol}//${cloudBucketName}.${endPoint}/${this.objectName}`;
      this.created && this.created(this.href);
    }
  };

  /**
   * 取消上传
   */
  uploadFileCancel = () => {
    this.client && this.client.cancel();
  };

  uploadFile = async () => {
    if (!this.client) {
      this.emitErrorListener("oss 初始化中！！");
      return;
    }
    try {
      const abortCheckpoint = this.getLocalStorageByTag();
      // startWithBreakpoint是否是断点续传
      this.startUpload &&
        this.startUpload({ startWithBreakpoint: !!abortCheckpoint });
      const res = await this.client.multipartUpload(
        this.objectName,
        this.file,
        {
          checkpoint: abortCheckpoint || "",
          progress: this.progress,
          // 不允许覆盖同名文件
          headers: {
            "x-oss-forbid-overwrite": "true",
          },
        }
      );
      this.isUploadReady = true;
      if (res && res.res && res.res.statusCode === 200) {
        // 上传成功，清空断点信息。
        this.cleanAbortInfo();
        // 上传成功回调。
        this.didUpload && this.didUpload();
      }
    } catch (err) {
      this.emitErrorListener(err);
    }
  };

  /**
   * 更新断点信息
   * @param {断点信息} cpt
   */
  updateCPT = (cpt) => {
    if (!cpt) return;
    try {
      localStorage.setItem(this.fileTag, JSON.stringify(cpt));
    } catch (error) {
      this.emitErrorListener(error);
    }
  };

  /**
   * 清空历史断点信息
   */
  cleanAbortInfo = () => {
    try {
      localStorage.setItem(this.fileTag, "");
    } catch (error) {
      this.emitErrorListener(error);
    }
  };

  // 获取分片上传进度、断点和返回值。
  progress = (p, cpt, res) => {
    if (res && res.statusCode === 200) {
      this.updateCPT(cpt);
      this.emitProgressListener(p);
      this.getCurNetSpeed(p, cpt, res);
    } else {
      this.emitErrorListener(res);
    }
  };

  getCurNetSpeed = (p, cpt, res) => {
    if (p && cpt && res) {
      const { partSize } = cpt;
      const { requestUrls } = res;
      const requestEntries = performance.getEntriesByName(requestUrls[0]);
      if (p > 0 && partSize && requestEntries && requestEntries[0]) {
        const { duration } = requestEntries[0];
        // speed = b / ms = kb / s
        const currentNetSpeed = Math.round(partSize / duration);
        this.emitNetSpeedListener(currentNetSpeed);
      }
    }
  };

  emitProgressListener = (data) =>
    this.progressObserverList.forEach((item) => item(data));

  addProgressListener = (fn) => {
    if (typeof fn !== "function") return;
    this.progressObserverList.push(fn);
  };

  getErrorInfo = (errMsg) => {
    let info = "上传异常，请稍后重试。";
    Object.keys(this.errorMapping).forEach((item) => {
      const { list, msg } = this.errorMapping[item];
      for (let err of list) {
        if (errMsg.indexOf(err) > -1) {
          info = msg;
          return;
        }
      }
    });
    return info;
  };

  emitErrorListener = (err) => {
    console.log("---uploadOSSError----", err);
    try {
      if (typeof err === "string") {
        this.errorObserverList.forEach((item) => item(err));
      } else if (err && (err.message || err.Message)) {
        const errInfo = this.getErrorInfo(err.message || err.Message);
        this.errorObserverList.forEach((item) => item(errInfo));
      }
    } catch (error) { }
  };

  addErrorListener = (fn) => {
    if (typeof fn !== "function") return;
    this.errorObserverList.push(fn);
  };

  emitNetSpeedListener = (speed) => {
    this.netSpeedObserverList.forEach((item) => item(speed));
  };

  addNetSpeedListener = (fn) => {
    if (typeof fn !== "function") return;
    this.netSpeedObserverList.push(fn);
  };
}

export default UploadOSS;



// WEBPACK FOOTER //
// ./src/lego/uploadOss.js